import { NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { NavigationDropdownComponent } from 'components/navigation-dropdown/navigation-dropdown.component';
import { Subscription } from 'rxjs';
import { AuthorizationService } from 'services/authorization.service';
import { NotificationService } from 'services/notification.service';
import { NavigationItem } from 'types/navigation-item.interface';
import { CLAIM_SCOPES, CLAIM_TYPES, UserNotificationListByUserIdItem } from 'types/user.interface';
import { checkClaims, isAdminUser, READING_ROOM_SERVICE_PROVIDER_ROLE, READING_ROOM_SERVICE_LEADER_ROLE } from 'util/user.utility';

@Component({
  selector: 'app-header-navigation',
  standalone: true,
  imports: [TranslateModule, NgClass, NavigationDropdownComponent, RouterLink],
  templateUrl: './header-navigation.component.html',
  styleUrl: './header-navigation.component.scss'
})
export class HeaderNavigationComponent implements OnInit, OnDestroy {
  unreadCount = 0;
  private notificationSub?: Subscription;
  navigationItems: NavigationItem[] = [
    {
      id: 8,
      title: 'components.navigation.notifications',
      url: '/pazinojumi',
      highlightPaths: ['/pazinojumi'],
      hasClaims: true
    },
    {
      id: 1,
      title: 'components.navigation.start',
      url: '/lasitavas-apmeklejums',
      highlightPaths: ['/lasitavas-apmeklejums'],
      hasClaims: checkClaims(CLAIM_SCOPES['serviceSubmission'], CLAIM_TYPES['read'])
    },
    {
      id: 7,
      title: 'components.navigation.infoRequest',
      url: '/informacijas-pieprasijums',
      highlightPaths: ['/informacijas-pieprasijums'],
      hasClaims: checkClaims(CLAIM_SCOPES['informationRequest'], CLAIM_TYPES['read'])
    },
    {
      id: 3,
      title: 'components.navigation.clientList',
      url: '/klienti',
      highlightPaths: ['/klienti', '/klients', '/juridiska-persona'],
      hasClaims: checkClaims(CLAIM_SCOPES['client'], CLAIM_TYPES['read'])
    },
    {
      id: 2,
      title: 'components.navigation.readingRoom',
      url: '/lasitavas',
      highlightPaths: ['/lasitavas', '/lasitava'],
      hasClaims: checkClaims(CLAIM_SCOPES['readingRoom'], CLAIM_TYPES['read'])
    },
    {
      id: 3,
      title: 'components.navigation.admin',
      url: '/klasifikatori',
      isMenuOpen: false,
      highlightPaths: ['/lietotaji', '/lietotajs', '/klasifikatori', '/strukturvienibas', '/lomas'],
      hasClaims: this.shouldRenderAdminMenu(),
      children: [
        {
          id: 1,
          title: 'components.navigation.classifiers',
          url: '/klasifikatori',
          hasClaims: checkClaims(CLAIM_SCOPES['classifier'], CLAIM_TYPES['read'])
        },
        {
          id: 2,
          title: 'components.navigation.departments',
          url: '/strukturvienibas',
          hasClaims: checkClaims(CLAIM_SCOPES['structuralUnit'], CLAIM_TYPES['read']) && !this.isReadingRoomServiceLeaderOnly()
        },
        {
          id: 3,
          title: 'components.navigation.roles',
          url: '/lomas',
          hasClaims: checkClaims(CLAIM_SCOPES['role'], CLAIM_TYPES['read'])
        },
        {
          id: 4,
          title: 'components.navigation.users',
          url: '/lietotaji',
          hasClaims: checkClaims(CLAIM_SCOPES['user'], CLAIM_TYPES['read'])
        },
        {
          id: 5,
          title: 'components.navigation.tooltips',
          url: '/uznirstosie-portala-padomi',
          hasClaims: isAdminUser()
        },
        {
          id: 6,
          title: 'components.navigation.audit',
          url: '/audits',
          hasClaims: isAdminUser()
        }
      ]
    }
  ];

  constructor(
    private router: Router,
    private authService: AuthorizationService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.notificationService.startPolling();
    this.notificationSub = this.notificationService.notifications$.subscribe((notifications) => {
      this.unreadCount = notifications?.list?.filter((n: UserNotificationListByUserIdItem) => !n.isRead).length ?? 0;
    });
  }

  ngOnDestroy(): void {
    this.notificationService.stopPolling();
    this.notificationSub?.unsubscribe();
  }

  public isRouteActive(id: number): boolean {
    const item = this.navigationItems.find((i: NavigationItem) => i.id === id);
    const urlTree = this.router.parseUrl(this.router.url);
    const activeSection = urlTree.queryParams['activeSection'];

    if (item?.url === '/lasitavas-apmeklejums' && activeSection) {
      return ['myApps', 'myReadingRoomApps', 'allApps'].includes(activeSection);
    }

    if (item?.highlightPaths) {
      const currentPath = this.router.url.split('?')[0];
      return item.highlightPaths.some((path: string) =>
        currentPath === path || currentPath.startsWith(path + '/')
      );
    }

    return this.router.url === item?.url;
  }

  shouldRenderAdminMenu(): boolean {
    return (
      (checkClaims(CLAIM_SCOPES['user'], CLAIM_TYPES['read']) ||
        checkClaims(CLAIM_SCOPES['role'], CLAIM_TYPES['read']) ||
        checkClaims(CLAIM_SCOPES['structuralUnit'], CLAIM_TYPES['read']) ||
        checkClaims(CLAIM_SCOPES['classifier'], CLAIM_TYPES['read'])) &&
      !this.isReadingRoomServiceProviderOnly()
    );
  }

  openMenu(item: NavigationItem): void {
    const index = this.navigationItems.findIndex((i: NavigationItem) => i.id === item.id);

    this.navigationItems[index].isMenuOpen = true;
  }

  closeMenu(item: NavigationItem): void {
    const index = this.navigationItems.findIndex((i: NavigationItem) => i.id === item.id);

    this.navigationItems[index].isMenuOpen = false;
  }

  hasChildrenWithClaims(item: NavigationItem): boolean {
    if (item.children) {
      return item.children.filter((child) => child.hasClaims).length > 0;
    }

    return false;
  }

  isReadingRoomServiceProviderOnly(): boolean {
    const roles = this.authService.userSecurityInfo().roleNames;

    return roles.includes(READING_ROOM_SERVICE_PROVIDER_ROLE) && roles.length === 2;
  }

  isReadingRoomServiceLeaderOnly(): boolean {
    const roles = this.authService.userSecurityInfo().roleNames;

    return roles.includes(READING_ROOM_SERVICE_LEADER_ROLE) && roles.length === 2;
  }
}
